/* eslint-disable react-refresh/only-export-components */
import type { RaRecord } from "ra-core";
import {
  EditBase,
  Form,
  useGetList,
  useInput,
  useNotify,
  useTranslate,
} from "ra-core";
import { useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  RotateCcw,
  Save,
  Trash2,
  Plus,
  GitBranch,
  FolderPlus,
  Palette,
  Database,
  Building2,
  TrendingUp,
  FileText,
  CheckSquare,
  LayoutGrid,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toSlug } from "@/lib/toSlug";
import { ArrayInput } from "@/components/admin/array-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { TextInput } from "@/components/admin/text-input";

import ImageEditorField from "../misc/ImageEditorField";
import {
  useConfigurationContext,
  useConfigurationUpdater,
  type ConfigurationContextValue,
} from "../root/ConfigurationContext";
import {
  defaultConfiguration,
  defaultDealPipelines,
} from "../root/defaultConfiguration";
import type { DealPipeline, DealStage } from "../types";
import {
  generateStageSlug,
  broadcastPipelinesChanged,
  getStorageKeyPipelines,
  getStorageKeyCurrentId,
} from "../deals/usePipelines";
import { DataFieldsSettings } from "./DataFieldsSettings";

const SECTIONS = [
  {
    id: "branding",
    label: "crm.settings.sections.branding",
    fallback: "Identidade Visual",
    icon: Palette,
  },
  {
    id: "data-fields",
    label: "crm.settings.sections.data_fields",
    fallback: "Campos e Dados",
    icon: Database,
  },
  {
    id: "companies",
    label: "resources.companies.name",
    fallback: "Empresas",
    icon: Building2,
  },
  {
    id: "deals",
    label: "resources.deals.name",
    fallback: "Negócios",
    icon: TrendingUp,
  },
  {
    id: "notes",
    label: "resources.notes.name",
    fallback: "Anotações",
    icon: FileText,
  },
  {
    id: "tasks",
    label: "resources.tasks.name",
    fallback: "Tarefas",
    icon: CheckSquare,
  },
  {
    id: "all",
    label: "crm.settings.sections.all",
    fallback: "Todas as Seções",
    icon: LayoutGrid,
  },
];

/** Ensure every item in a { value, label } array has a value (slug from label). */
const ensureValues = (items: { value?: string; label: string }[] | undefined) =>
  items
    ?.filter((item) => item && (item.label?.trim() || item.value?.trim()))
    .map((item) => ({ ...item, value: item.value || toSlug(item.label) }));

type ValidateItemsInUseMessages = {
  duplicate?: (displayName: string, duplicates: string[]) => string;
  inUse?: (displayName: string, inUse: string[]) => string;
  validating?: string;
};

/**
 * Validate that no items were removed if they are still referenced by existing deals.
 * Also rejects duplicate slug values.
 * Returns undefined if valid, or an error message string.
 */
export const validateItemsInUse = (
  items: { value: string; label: string }[] | undefined,
  deals: RaRecord[] | undefined,
  fieldName: string,
  displayName: string,
  messages?: ValidateItemsInUseMessages,
) => {
  if (!items) return undefined;
  // Ignore items with empty label and value
  const validItems = items.filter(
    (i) => i && (i.label?.trim() || i.value?.trim()),
  );
  // Check for duplicate slugs
  const slugs = validItems.map((i) => i.value || toSlug(i.label));
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const slug of slugs) {
    if (seen.has(slug)) duplicates.add(slug);
    seen.add(slug);
  }
  if (duplicates.size > 0) {
    const duplicatesList = [...duplicates];
    return (
      messages?.duplicate?.(displayName, duplicatesList) ??
      `Duplicate ${displayName}: ${duplicatesList.join(", ")}`
    );
  }
  // Check that no in-use value was removed (skip if deals haven't loaded)
  if (!deals) {
    // Only return validating string if explicitly requested by caller (e.g. unit tests)
    return messages?.validating ? messages.validating : undefined;
  }
  // If inUse message handler is defined, check in-use items
  if (messages?.inUse) {
    const values = new Set(slugs);
    const inUse = [
      ...new Set(
        deals
          .filter(
            (deal) => deal[fieldName] && !values.has(deal[fieldName] as string),
          )
          .map((deal) => deal[fieldName] as string),
      ),
    ];
    if (inUse.length > 0) {
      return (
        messages.inUse(displayName, inUse) ??
        `Cannot remove ${displayName} that are still used by deals: ${inUse.join(", ")}`
      );
    }
  }
  return undefined;
};

const getCurrencyChoices = () => {
  const displayNames = new Intl.DisplayNames(
    typeof navigator !== "undefined"
      ? (navigator.languages as string[])
      : ["en"],
    { type: "currency" },
  );
  return Intl.supportedValuesOf("currency").map((code) => ({
    id: code,
    name: `${code} – ${displayNames.of(code)}`,
  }));
};

const transformFormValues = (data: Record<string, any>) => ({
  config: {
    title: data.title,
    lightModeLogo: data.lightModeLogo,
    darkModeLogo: data.darkModeLogo,
    currency: data.currency,
    companySectors: ensureValues(data.companySectors),
    dealCategories: ensureValues(data.dealCategories),
    taskTypes: ensureValues(data.taskTypes),
    dealStages: ensureValues(data.dealStages),
    dealPipelines: data.dealPipelines,
    currentPipelineId: data.currentPipelineId,
    dealPipelineStatuses: data.dealPipelineStatuses,
    noteStatuses: ensureValues(data.noteStatuses),
  } as ConfigurationContextValue,
});

export const SettingsPage = () => {
  const updateConfiguration = useConfigurationUpdater();
  const notify = useNotify();

  return (
    <EditBase
      resource="configuration"
      id={1}
      mutationMode="pessimistic"
      redirect={false}
      redirectOnError={false}
      transform={transformFormValues}
      mutationOptions={{
        onSuccess: (data: any) => {
          updateConfiguration(data.config);
          if (data?.config?.dealPipelines) {
            try {
              localStorage.setItem(
                getStorageKeyPipelines(),
                JSON.stringify(data.config.dealPipelines),
              );
              if (data.config.currentPipelineId) {
                localStorage.setItem(
                  getStorageKeyCurrentId(),
                  data.config.currentPipelineId,
                );
              }
              broadcastPipelinesChanged(
                data.config.dealPipelines,
                data.config.currentPipelineId ||
                  data.config.dealPipelines[0]?.id ||
                  "principal",
              );
            } catch (err) {
              console.error(err);
            }
          }
          notify("crm.settings.saved");
        },
        onError: () => {
          notify("crm.settings.save_error", {
            type: "error",
          });
        },
      }}
    >
      <SettingsForm />
    </EditBase>
  );
};

SettingsPage.path = "/settings";

const SettingsForm = () => {
  const config = useConfigurationContext();

  const defaultValues = useMemo(
    () => ({
      title: config.title,
      lightModeLogo: { src: config.lightModeLogo },
      darkModeLogo: { src: config.darkModeLogo },
      currency: config.currency,
      companySectors: config.companySectors,
      dealCategories: config.dealCategories,
      taskTypes: config.taskTypes,
      dealStages: config.dealStages,
      dealPipelines: config.dealPipelines || defaultDealPipelines,
      currentPipelineId: config.currentPipelineId || defaultDealPipelines[0].id,
      dealPipelineStatuses: config.dealPipelineStatuses,
      noteStatuses: config.noteStatuses,
    }),
    [config],
  );

  return (
    <Form defaultValues={defaultValues}>
      <SettingsFormFields />
    </Form>
  );
};

const PipelineSettingsSection = () => {
  const { watch, setValue } = useFormContext();
  const updateConfiguration = useConfigurationUpdater();
  const dealPipelines: DealPipeline[] =
    watch("dealPipelines") || defaultDealPipelines;
  const currentPipelineId: string =
    watch("currentPipelineId") || dealPipelines[0]?.id || "principal";
  const dealPipelineStatuses: string[] = watch("dealPipelineStatuses") || [];

  const [selectedPipeId, setSelectedPipeId] = useState<string>(
    currentPipelineId || dealPipelines[0]?.id || "principal",
  );
  const [newStageName, setNewStageName] = useState("");
  const [newPipeName, setNewPipeName] = useState("");
  const [newPipeDesc, setNewPipeDesc] = useState("");
  const [showNewPipeModal, setShowNewPipeModal] = useState(false);

  const activePipe =
    dealPipelines.find((p) => p.id === selectedPipeId) ||
    dealPipelines[0] ||
    defaultDealPipelines[0];

  const syncPipelinesAndStore = (
    updated: DealPipeline[],
    activeId?: string,
  ) => {
    const targetId = activeId || selectedPipeId;
    const active = updated.find((p) => p.id === targetId) || updated[0];

    setValue("dealPipelines", updated, { shouldDirty: true });
    if (active) {
      setValue("dealStages", active.etapas, { shouldDirty: true });
      setValue("currentPipelineId", active.id, { shouldDirty: true });
    }

    try {
      localStorage.setItem(getStorageKeyPipelines(), JSON.stringify(updated));
      localStorage.setItem(getStorageKeyCurrentId(), active?.id || targetId);
    } catch (err) {
      console.error("Erro ao salvar pipelines no localStorage:", err);
    }

    broadcastPipelinesChanged(updated, active?.id || targetId);

    updateConfiguration((prev) => ({
      ...prev,
      dealPipelines: updated,
      dealStages: active ? active.etapas : prev?.dealStages || [],
      currentPipelineId: active?.id || targetId,
    }));
  };

  const handleSelectPipe = (id: string) => {
    setSelectedPipeId(id);
    const target = dealPipelines.find((p) => p.id === id);
    if (target) {
      syncPipelinesAndStore(dealPipelines, id);
    }
  };

  const handleUpdatePipeName = (name: string) => {
    const updated = dealPipelines.map((p) =>
      p.id === activePipe.id ? { ...p, nome: name } : p,
    );
    syncPipelinesAndStore(updated);
  };

  const handleUpdatePipeDesc = (desc: string) => {
    const updated = dealPipelines.map((p) =>
      p.id === activePipe.id ? { ...p, descricao: desc } : p,
    );
    syncPipelinesAndStore(updated);
  };

  const handleUpdateStageLabel = (stageValue: string, label: string) => {
    const updated = dealPipelines.map((p) => {
      if (p.id !== activePipe.id) return p;
      return {
        ...p,
        etapas: p.etapas.map((s) =>
          s.value === stageValue ? { ...s, label } : s,
        ),
      };
    });
    syncPipelinesAndStore(updated);
  };

  const handleAddStage = () => {
    if (!newStageName.trim()) return;
    const clean = newStageName.trim();
    const slug = generateStageSlug(clean, activePipe.etapas);
    const newStage: DealStage = { value: slug, label: clean };

    const updated = dealPipelines.map((p) => {
      if (p.id !== activePipe.id) return p;
      const etapas = [...p.etapas];
      const wonIdx = etapas.findIndex(
        (s) => s.value === "won" || s.value === "lost",
      );
      if (wonIdx >= 0) etapas.splice(wonIdx, 0, newStage);
      else etapas.push(newStage);
      return { ...p, etapas };
    });

    syncPipelinesAndStore(updated);
    setNewStageName("");
  };

  const handleDeleteStage = (stageValue: string) => {
    const updated = dealPipelines.map((p) => {
      if (p.id !== activePipe.id) return p;
      return {
        ...p,
        etapas: p.etapas.filter((s) => s.value !== stageValue),
      };
    });
    syncPipelinesAndStore(updated);
  };

  const handleCreatePipeline = () => {
    if (!newPipeName.trim()) return;
    let baseSlug = newPipeName
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    if (!baseSlug) baseSlug = "funil";
    let slug = baseSlug;
    let counter = 1;
    while (dealPipelines.some((p) => p.id === slug)) {
      counter++;
      slug = `${baseSlug}-${counter}`;
    }

    const newPipe: DealPipeline = {
      id: slug,
      nome: newPipeName.trim(),
      descricao: newPipeDesc.trim(),
      etapas: [...defaultDealPipelines[0].etapas],
    };
    const updated = [...dealPipelines, newPipe];
    setSelectedPipeId(newPipe.id);
    syncPipelinesAndStore(updated, newPipe.id);
    setNewPipeName("");
    setNewPipeDesc("");
    setShowNewPipeModal(false);
  };

  const handleDeletePipeline = () => {
    if (dealPipelines.length <= 1) return;
    if (!window.confirm("Deseja realmente excluir este funil?")) return;
    const updated = dealPipelines.filter((p) => p.id !== activePipe.id);
    const nextId = updated[0].id;
    setSelectedPipeId(nextId);
    syncPipelinesAndStore(updated, nextId);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-muted/40 rounded-xl border">
        <div className="flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-muted-foreground" />
          <Label className="text-xs font-semibold">
            Funil para Configurar:
          </Label>
          <Select value={selectedPipeId} onValueChange={handleSelectPipe}>
            <SelectTrigger className="h-8 w-60 text-xs bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {dealPipelines.map((pipe) => (
                <SelectItem key={pipe.id} value={pipe.id} className="text-xs">
                  {pipe.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 text-xs gap-1"
            onClick={() => setShowNewPipeModal(true)}
          >
            <FolderPlus className="w-3.5 h-3.5" />
            Novo Funil
          </Button>

          {dealPipelines.length > 1 && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-8 text-xs text-muted-foreground hover:text-destructive gap-1"
              onClick={handleDeletePipeline}
            >
              <Trash2 className="w-3.5 h-3.5" />
              Excluir Funil
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Nome do Funil</Label>
          <Input
            value={activePipe.nome}
            onChange={(e) => handleUpdatePipeName(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Descrição do Funil</Label>
          <Input
            value={activePipe.descricao || ""}
            onChange={(e) => handleUpdatePipeDesc(e.target.value)}
            placeholder="Ex: Pipeline comercial contábil"
            className="h-8 text-xs"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-semibold">
          Etapas deste Funil (Stages)
        </Label>
        <p className="text-xs text-muted-foreground">
          Edite o nome das etapas ou adicione novas etapas ao funil selecionado:
        </p>

        <div className="space-y-1.5 border rounded-lg p-3 bg-card">
          {activePipe.etapas.map((stage) => {
            const isProtected = stage.value === "won" || stage.value === "lost";
            return (
              <div key={stage.value} className="flex items-center gap-2">
                <Input
                  value={stage.label}
                  onChange={(e) =>
                    handleUpdateStageLabel(stage.value, e.target.value)
                  }
                  className="h-8 text-xs flex-1"
                />
                <span className="text-[11px] font-mono text-muted-foreground px-2 py-1 bg-muted rounded">
                  {stage.value}
                </span>
                {!isProtected && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => handleDeleteStage(stage.value)}
                    title="Excluir etapa"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            );
          })}

          <div className="flex items-center gap-2 pt-2 border-t mt-2">
            <Input
              placeholder="Nome da nova etapa..."
              value={newStageName}
              onChange={(e) => setNewStageName(e.target.value)}
              className="h-8 text-xs flex-1"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddStage();
                }
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 text-xs gap-1"
              onClick={handleAddStage}
              disabled={!newStageName.trim()}
            >
              <Plus className="w-3.5 h-3.5" />
              Adicionar Etapa
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs font-semibold">
          Etapas de Ganho (Pipeline Statuses)
        </Label>
        <p className="text-xs text-muted-foreground">
          Selecione quais etapas contam como negócio fechado/ganho nas métricas
          e relatórios:
        </p>
        <div className="flex flex-wrap gap-2">
          {activePipe.etapas.map((stage) => {
            const isSelected = dealPipelineStatuses.includes(stage.value);
            return (
              <Button
                key={stage.value}
                type="button"
                variant={isSelected ? "default" : "outline"}
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  if (isSelected) {
                    setValue(
                      "dealPipelineStatuses",
                      dealPipelineStatuses.filter((s) => s !== stage.value),
                    );
                  } else {
                    setValue("dealPipelineStatuses", [
                      ...dealPipelineStatuses,
                      stage.value,
                    ]);
                  }
                }}
              >
                {stage.label || stage.value}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Modal Novo Funil */}
      <Dialog open={showNewPipeModal} onOpenChange={setShowNewPipeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">
              Criar Novo Funil de Vendas
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs">Nome do Funil</Label>
              <Input
                placeholder="Ex: Funil BPO Financeiro..."
                value={newPipeName}
                onChange={(e) => setNewPipeName(e.target.value)}
                className="text-xs h-8"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Descrição (opcional)</Label>
              <Input
                placeholder="Ex: Pipeline específico para serviços de gestão financeira"
                value={newPipeDesc}
                onChange={(e) => setNewPipeDesc(e.target.value)}
                className="text-xs h-8"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowNewPipeModal(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleCreatePipeline}
              disabled={!newPipeName.trim()}
            >
              Criar Funil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SettingsFormFields = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const currencyChoices = useMemo(() => getCurrencyChoices(), []);
  const [activeSection, setActiveSection] = useState<string>("branding");
  const {
    reset,
    formState: { isSubmitting, errors },
  } = useFormContext();

  const handleSaveClick = () => {
    if (errors && Object.keys(errors).length > 0) {
      console.warn("Erros no formulário de configurações:", errors);
      const fieldLabels: Record<string, string> = {
        title: "Título do Aplicativo",
        currency: "Moeda",
        companySectors: "Setores de Empresas",
        dealCategories: "Categorias de Negócios",
        noteStatuses: "Status das Anotações",
        taskTypes: "Tipos de Tarefas",
        dealPipelines: "Funis de Vendas",
      };
      const fieldSectionMap: Record<string, string> = {
        title: "branding",
        lightModeLogo: "branding",
        darkModeLogo: "branding",
        companySectors: "companies",
        currency: "deals",
        dealPipelines: "deals",
        dealStages: "deals",
        dealPipelineStatuses: "deals",
        dealCategories: "deals",
        noteStatuses: "notes",
        taskTypes: "tasks",
      };
      const errorNames = Object.keys(errors)
        .map((key) => fieldLabels[key] || key)
        .join(", ");
      notify(`Por favor, verifique os campos: ${errorNames}`, {
        type: "error",
      });
      const firstError = Object.keys(errors)[0];
      if (firstError && fieldSectionMap[firstError]) {
        setActiveSection(fieldSectionMap[firstError]);
      }
      setTimeout(() => {
        const targetElement =
          document.getElementById(firstError) ||
          document.querySelector(`[name="${firstError}"]`);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
  };

  const categoryDisplayName = translate(
    "crm.settings.validation.entities.categories",
  );

  const { data: deals } = useGetList("deals", {
    pagination: { page: 1, perPage: 1000 },
  });

  const validateDealCategories = useCallback(
    (categories: { value: string; label: string }[] | undefined) =>
      validateItemsInUse(categories, deals, "category", categoryDisplayName, {
        duplicate: (displayName, duplicates) =>
          translate("crm.settings.validation.duplicate", {
            display_name: displayName,
            items: duplicates.join(", "),
          }),
      }),
    [categoryDisplayName, deals, translate],
  );

  return (
    <div className="flex flex-col md:flex-row gap-8 mt-2 pb-12">
      {/* Left navigation */}
      <nav className="w-full md:w-56 shrink-0">
        <div className="md:sticky md:top-4 space-y-2">
          <div>
            <h1 className="text-xl font-bold px-3 text-foreground">
              {translate("crm.settings.title", { _: "Configurações" })}
            </h1>
            <p className="text-xs text-muted-foreground px-3 mt-0.5">
              Preferências gerais e parâmetros do CRM.
            </p>
          </div>

          <div className="flex flex-row md:flex-col gap-1 overflow-x-auto pb-2 md:pb-0 scrollbar-none">
            {SECTIONS.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveSection(section.id)}
                  className={cn(
                    "flex items-center gap-2.5 px-3 py-2 text-xs font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap text-left",
                    isActive
                      ? "bg-primary text-primary-foreground font-semibold shadow-xs"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/80",
                  )}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>
                    {translate(section.label, {
                      smart_count: 2,
                      _: section.fallback,
                    })}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main content */}
      <div className="flex-1 min-w-0 max-w-4xl space-y-6">
        {/* Branding */}
        <div
          className={
            activeSection === "branding" || activeSection === "all"
              ? "block"
              : "hidden"
          }
        >
          <Card id="branding">
            <CardContent className="space-y-4">
              <h2 className="text-xl font-semibold text-muted-foreground">
                {translate("crm.settings.sections.branding", {
                  _: "Identidade Visual",
                })}
              </h2>
              <TextInput
                source="title"
                label={translate("crm.settings.app_title", {
                  _: "Título do Aplicativo",
                })}
              />
              <div className="flex gap-8">
                <div className="flex flex-col items-center gap-1">
                  <p className="text-sm text-muted-foreground">
                    {translate("crm.settings.light_mode_logo", {
                      _: "Logo (Modo Claro)",
                    })}
                  </p>
                  <ImageEditorField
                    source="lightModeLogo"
                    width={100}
                    height={100}
                    linkPosition="bottom"
                    backgroundImageColor="#f5f5f5"
                  />
                </div>
                <div className="flex flex-col items-center gap-1">
                  <p className="text-sm text-muted-foreground">
                    {translate("crm.settings.dark_mode_logo", {
                      _: "Logo (Modo Escuro)",
                    })}
                  </p>
                  <ImageEditorField
                    source="darkModeLogo"
                    width={100}
                    height={100}
                    linkPosition="bottom"
                    backgroundImageColor="#1a1a1a"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Data Fields & Custom Fields */}
        <div
          id="data-fields"
          className={
            activeSection === "data-fields" || activeSection === "all"
              ? "block"
              : "hidden"
          }
        >
          <DataFieldsSettings />
        </div>

        {/* Companies */}
        <div
          className={
            activeSection === "companies" || activeSection === "all"
              ? "block"
              : "hidden"
          }
        >
          <Card id="companies">
            <CardContent className="space-y-4">
              <h2 className="text-xl font-semibold text-muted-foreground">
                {translate("resources.companies.name", {
                  smart_count: 2,
                  _: "Empresas",
                })}
              </h2>
              <h3 className="text-lg font-medium text-muted-foreground">
                {translate("crm.settings.companies.sectors", {
                  _: "Setores de Atuação",
                })}
              </h3>
              <ArrayInput
                source="companySectors"
                label={false}
                helperText={false}
              >
                <SimpleFormIterator disableReordering disableClear>
                  <TextInput source="label" label={false} />
                </SimpleFormIterator>
              </ArrayInput>
            </CardContent>
          </Card>
        </div>

        {/* Deals */}
        <div
          className={
            activeSection === "deals" || activeSection === "all"
              ? "block"
              : "hidden"
          }
        >
          <Card id="deals">
            <CardContent className="space-y-4">
              <h2 className="text-xl font-semibold text-muted-foreground">
                {translate("resources.deals.name", {
                  smart_count: 2,
                  _: "Negócios",
                })}
              </h2>
              <h3 className="text-lg font-medium text-muted-foreground">
                {translate("crm.settings.deals.currency", {
                  _: "Moeda",
                })}
              </h3>
              <AutocompleteInput
                source="currency"
                label={false}
                choices={currencyChoices}
                inputText={(choice) => choice?.id}
                modal
              />

              <Separator />

              <PipelineSettingsSection />

              <Separator />

              <h3 className="text-lg font-medium text-muted-foreground">
                {translate("crm.settings.deals.categories", {
                  _: "Categorias de Negócios",
                })}
              </h3>
              <ArrayInput
                source="dealCategories"
                label={false}
                helperText={false}
                validate={validateDealCategories}
              >
                <SimpleFormIterator disableReordering disableClear>
                  <TextInput source="label" label={false} />
                </SimpleFormIterator>
              </ArrayInput>
            </CardContent>
          </Card>
        </div>

        {/* Notes */}
        <div
          className={
            activeSection === "notes" || activeSection === "all"
              ? "block"
              : "hidden"
          }
        >
          <Card id="notes">
            <CardContent className="space-y-4">
              <h2 className="text-xl font-semibold text-muted-foreground">
                {translate("resources.notes.name", {
                  smart_count: 2,
                  _: "Anotações",
                })}
              </h2>
              <h3 className="text-lg font-medium text-muted-foreground">
                {translate("crm.settings.notes.statuses", {
                  _: "Status / Temperaturas das Anotações",
                })}
              </h3>
              <ArrayInput
                source="noteStatuses"
                label={false}
                helperText={false}
              >
                <SimpleFormIterator inline disableReordering disableClear>
                  <TextInput source="label" label={false} className="flex-1" />
                  <ColorInput source="color" />
                </SimpleFormIterator>
              </ArrayInput>
            </CardContent>
          </Card>
        </div>

        {/* Tasks */}
        <div
          className={
            activeSection === "tasks" || activeSection === "all"
              ? "block"
              : "hidden"
          }
        >
          <Card id="tasks">
            <CardContent className="space-y-4">
              <h2 className="text-xl font-semibold text-muted-foreground">
                {translate("resources.tasks.name", {
                  smart_count: 2,
                  _: "Tarefas",
                })}
              </h2>
              <h3 className="text-lg font-medium text-muted-foreground">
                {translate("crm.settings.tasks.types", {
                  _: "Tipos de Tarefas",
                })}
              </h3>
              <ArrayInput source="taskTypes" label={false} helperText={false}>
                <SimpleFormIterator disableReordering disableClear>
                  <TextInput source="label" label={false} />
                </SimpleFormIterator>
              </ArrayInput>
            </CardContent>
          </Card>
        </div>

        {/* Action buttons (Restaurar, Cancelar, Salvar) */}
        <div className="sticky bottom-0 z-20 border-t border-border/60 bg-background/95 backdrop-blur-md -mx-2 sm:-mx-6 px-4 sm:px-6 py-3.5 mt-8 rounded-b-xl flex flex-wrap items-center justify-between gap-3 shadow-md">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() =>
              reset({
                ...defaultConfiguration,
                lightModeLogo: {
                  src: defaultConfiguration.lightModeLogo,
                },
                darkModeLogo: { src: defaultConfiguration.darkModeLogo },
              })
            }
          >
            <RotateCcw className="h-4 w-4 mr-1.5" />
            {translate("crm.settings.reset_defaults", {
              _: "Restaurar Padrões",
            })}
          </Button>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => window.history.back()}
            >
              {translate("ra.action.cancel", { _: "Cancelar" })}
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={isSubmitting}
              onClick={handleSaveClick}
            >
              <Save className="h-4 w-4 mr-1.5" />
              {isSubmitting
                ? translate("crm.settings.saving", { _: "Salvando..." })
                : translate("ra.action.save", { _: "Salvar" })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** A minimal color picker input compatible with ra-core's useInput. */
const ColorInput = ({ source }: { source: string }) => {
  const { field } = useInput({ source });
  return (
    <input
      type="color"
      {...field}
      value={field.value || "#000000"}
      className="w-9 h-9 shrink-0 cursor-pointer appearance-none rounded border bg-transparent p-0.5 [&::-webkit-color-swatch-wrapper]:cursor-pointer [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:cursor-pointer [&::-webkit-color-swatch]:rounded-sm [&::-webkit-color-swatch]:border-none [&::-moz-color-swatch]:cursor-pointer [&::-moz-color-swatch]:rounded-sm [&::-moz-color-swatch]:border-none"
    />
  );
};
